import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["componentList", "componentTemplate", "itemsContainer", "headerContainer", "summaryTotals", "suggestion"]
  static values = { currencySymbol: String }

  connect() {
    this.updateItemCostFields()
    this.refreshSuggestions()

    // Watch for new supply items being added via nested-form
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          this.updateItemCostFields()
        }
      })
    })

    if (this.hasItemsContainerTarget) {
      this.observer.observe(this.itemsContainerTarget, { childList: true })
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  addComponent(event) {
    event.preventDefault()
    this.insertComponent(this.generateId(), "")
    this.componentListTarget.querySelector('.cost-component-row:last-child input[name*="[name]"]')?.focus()
  }

  // Brings back one of the store's components, keeping its id so its costs
  // line up with the other supplies that use it.
  addSharedComponent(event) {
    event.preventDefault()
    // Read from the dataset: Stimulus params would turn an id like "1e40" into a number.
    const { componentId, componentName } = event.currentTarget.dataset
    this.insertComponent(componentId, componentName)
  }

  insertComponent(id, name) {
    const position = this.componentListTarget.querySelectorAll('.cost-component-row').length + 1

    const index = Date.now()
    const template = this.componentTemplateTarget.innerHTML
      .replace(/COMPONENT_ID/g, id)
      .replace(/COMPONENT_INDEX/g, index)
      .replace(/COMPONENT_POSITION/g, position)

    this.componentListTarget.insertAdjacentHTML('beforeend', template)
    this.componentListTarget.querySelector('.cost-component-row:last-child input[name*="[name]"]').value = name
    this.updateItemCostFields()
    this.updateSummaryRow()
    this.refreshSuggestions()
  }

  removeComponent(event) {
    event.preventDefault()
    const row = event.target.closest('.cost-component-row')
    if (row) {
      const componentId = row.dataset.componentId
      row.remove()
      this.removeComponentFromItems(componentId)
      this.updateItemCostFields()
      this.updateSummaryRow()
      this.refreshSuggestions()
    }
  }

  // Offers only the store components this supply does not already have.
  refreshSuggestions() {
    const present = new Set(this.getComponents().map(c => c.id))
    this.suggestionTargets.forEach(button => {
      button.hidden = present.has(button.dataset.componentId)
    })
  }

  componentNameChanged(event) {
    this.updateItemCostFields()
    this.updateSummaryRow()
  }

  updateSummaryRow() {
    const components = this.getComponents()
    const symbol = this.hasCurrencySymbolValue ? this.currencySymbolValue : ''

    // Update header row
    if (this.hasHeaderContainerTarget) {
      this.headerContainerTarget.innerHTML = ''
      components.forEach(component => {
        if (!component.name) return
        const headerHtml = `<div class="w-16 truncate text-xs" title="${component.name}">${component.name}</div>`
        this.headerContainerTarget.insertAdjacentHTML('beforeend', headerHtml)
      })
    }

    // Update summary totals row
    if (this.hasSummaryTotalsTarget) {
      this.summaryTotalsTarget.innerHTML = ''
      components.forEach(component => {
        if (!component.name) return
        const totalHtml = `<div class="w-16 text-right text-xs text-slate-600 dark:text-slate-400" data-component-total="${component.id}">${symbol}0.00</div>`
        this.summaryTotalsTarget.insertAdjacentHTML('beforeend', totalHtml)
      })
    }
  }

  updateItemCostFields() {
    const components = this.getComponents()
    if (!this.hasItemsContainerTarget) return
    const itemContainers = this.itemsContainerTarget.querySelectorAll('.supply-item')

    itemContainers.forEach((item) => {
      const costFieldsContainer = item.querySelector('.cost-components-fields')
      if (!costFieldsContainer) return

      // Get the item index from existing form fields (e.g., supply[supply_items_attributes][123456][quantity])
      const quantityInput = item.querySelector('input[name*="[quantity]"]')
      if (!quantityInput) return

      const match = quantityInput.name.match(/supply\[supply_items_attributes\]\[([^\]]+)\]/)
      const itemIndex = match ? match[1] : costFieldsContainer.dataset.itemIndex

      // Get existing values
      const existingValues = {}
      costFieldsContainer.querySelectorAll('input[data-component-id]').forEach(input => {
        existingValues[input.dataset.componentId] = input.value
      })

      // Rebuild cost fields
      costFieldsContainer.innerHTML = ''

      components.forEach(component => {
        if (!component.name) return

        const value = existingValues[component.id] || ''
        const symbol = this.hasCurrencySymbolValue ? this.currencySymbolValue : '$'
        const fieldHtml = `
          <div class="w-16">
            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-1 flex items-center text-slate-400 text-xs">${symbol}</span>
              <input type="number"
                name="supply[supply_items_attributes][${itemIndex}][cost_components][${component.id}]"
                value="${value}"
                step="0.01"
                min="0"
                placeholder="0.0"
                title="${component.name}"
                data-component-id="${component.id}"
                data-supply-item-pricing-target="component"
                data-action="input->supply-item-pricing#calculate"
                class="block w-full pl-3 pr-0 rounded border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white text-xs focus:ring-teal-500 focus:border-teal-500 py-1 placeholder-slate-400 dark:placeholder-slate-500">
            </div>
          </div>
        `
        costFieldsContainer.insertAdjacentHTML('beforeend', fieldHtml)
      })

      // Trigger recalculation on the supply-item-pricing controller
      const pricingController = this.application.getControllerForElementAndIdentifier(item, 'supply-item-pricing')
      if (pricingController) {
        pricingController.calculate()
      }
    })
  }

  removeComponentFromItems(componentId) {
    if (!this.hasItemsContainerTarget) return
    const inputs = this.itemsContainerTarget.querySelectorAll(`input[data-component-id="${componentId}"]`)
    inputs.forEach(input => {
      const wrapper = input.closest('.w-16')
      if (wrapper) wrapper.remove()
    })
  }

  getComponents() {
    const components = []
    this.componentListTarget.querySelectorAll('.cost-component-row').forEach((row, index) => {
      const idInput = row.querySelector('input[name*="[id]"]')
      const nameInput = row.querySelector('input[name*="[name]"]')
      if (idInput && nameInput) {
        components.push({
          id: idInput.value,
          name: nameInput.value,
          position: index + 1
        })
      }
    })
    return components
  }

  generateId() {
    return Math.random().toString(36).substring(2, 10)
  }
}
